import React, {useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {PriceUpdateBatch, updatePanelPrices} from '../../../api/panelPriceUpdate'

const emptyTotals = {processed: 0, updated: 0, failed: [] as PriceUpdateBatch['failed']}

export default function RefreshPanelPrices({onUpdated}: {onUpdated: () => void}) {
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const requestRunning = useRef(false)
  const [totals, setTotals] = useState(emptyTotals)
  const [batchCount, setBatchCount] = useState(0)
  const [nextId, setNextId] = useState<number | null>(0)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const runBatch = async (afterId: number) => {
    if (requestRunning.current) return
    requestRunning.current = true
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const result = await updatePanelPrices(afterId)
      const batch = result.data
      setTotals((previous) => ({
        processed: previous.processed + batch.processed,
        updated: previous.updated + batch.updated,
        failed: [...previous.failed, ...batch.failed],
      }))
      setBatchCount((count) => count + 1)
      setNextId(batch.has_more ? batch.next_after_id : null)
      if (result.error) setMessage(result.message)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Osvježavanje nije uspjelo.')
    } finally {
      requestRunning.current = false
      setBusy(false)
      // Even a lost response can follow successful writes on the server.
      onUpdated()
    }
  }

  const start = () => {
    if (requestRunning.current) return
    setTotals(emptyTotals)
    setBatchCount(0)
    setNextId(0)
    setShow(true)
    void runBatch(0)
  }

  return (
    <>
      <button type='button' className='panel-shortcut panel-shortcut-price' onClick={start}>
        <span className='panel-shortcut-icon'>
          <i className='bi bi-arrow-repeat fs-2' />
        </span>
        <span>
          <span className='d-block fw-bold'>Osvježi cijene</span>
          <span className='d-block fs-8 text-muted'>Preuzmi cijene naloga</span>
        </span>
        <i className='bi bi-chevron-right ms-auto' />
      </button>
      <Modal
        show={show}
        onHide={() => !requestRunning.current && setShow(false)}
        backdrop={busy ? 'static' : true}
        keyboard={!busy}
        centered
        size='lg'
        fullscreen='sm-down'
        scrollable
        aria-labelledby='price-update-title'
      >
        <Modal.Header closeButton={!busy} className='px-5 py-5'>
          <div>
            <div className='text-primary fw-semibold fs-7 mb-1'>Paneli · Cijene naloga</div>
            <h2 id='price-update-title' className='mb-1'>
              Osvježavanje cijena
            </h2>
            <div className='text-muted fs-7'>
              Pregled obrade i naloga kojima cijena nije ažurirana.
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className='p-5 bg-light'>
          <div className='row g-3 mb-5'>
            {[
              {label: 'Obrađeno', value: totals.processed, color: 'text-gray-900'},
              {label: 'Ažurirano', value: totals.updated, color: 'text-success'},
              {label: 'Neuspješno', value: totals.failed.length, color: 'text-danger'},
            ].map(({label, value, color}) => (
              <div className='col-4' key={label}>
                <div className='bg-white border rounded p-3 p-sm-5 h-100'>
                  <div className='text-muted fs-8 mb-2'>{label}</div>
                  <div className={`fs-2 fw-bold ${color}`}>{value.toLocaleString('bs-BA')}</div>
                </div>
              </div>
            ))}
          </div>
          <div
            role='status'
            aria-live='polite'
            className='bg-white border rounded p-4 mb-4 d-flex gap-3 align-items-center'
          >
            {busy && (
              <span
                className='spinner-border spinner-border-sm text-primary flex-shrink-0'
                aria-hidden='true'
              />
            )}
            <div>
              <div className='fw-bold'>
                {busy
                  ? 'Osvježavanje je u toku…'
                  : error
                  ? 'Obrada je prekinuta'
                  : nextId !== null
                  ? 'Spremno za nastavak'
                  : 'Obrada je završena'}
              </div>
              <div className='text-muted fs-7'>
                {busy
                  ? 'Sačekajte odgovor servera. Ovo može potrajati.'
                  : nextId !== null
                  ? 'Za sljedeću grupu naloga odaberite nastavak ispod.'
                  : totals.failed.length
                  ? 'Provjerite neuspješne naloge u listi ispod.'
                  : 'Sve vraćene cijene su uspješno ažurirane.'}
              </div>
              <div className='text-muted fs-8 mt-1'>Završene grupe: {batchCount}</div>
            </div>
          </div>
          {error && (
            <div role='alert' className='alert alert-danger mb-4'>
              {error}
            </div>
          )}
          {message && (
            <div role='alert' className='alert alert-warning mb-4'>
              {message}
            </div>
          )}
          {totals.failed.length > 0 && (
            <section aria-labelledby='price-update-failures'>
              <h3 id='price-update-failures' className='fs-5 mb-3'>
                Nalozi koji nisu ažurirani ({totals.failed.length})
              </h3>
              <div className='d-flex flex-column gap-3'>
                {totals.failed.map((failure, index) => (
                  <div
                    key={`${failure.id}-${index}`}
                    className='bg-white border border-danger rounded p-4'
                  >
                    <div className='d-flex flex-wrap justify-content-between gap-2 mb-2'>
                      <span className='fw-bold text-gray-900'>Nalog {failure.brojNaloga}</span>
                      <span className='text-muted fs-8'>ID: {failure.id}</span>
                    </div>
                    <div className='text-danger fs-7' style={{overflowWrap: 'anywhere'}}>
                      {failure.error}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </Modal.Body>
        <Modal.Footer className='p-4 gap-2'>
          <button
            type='button'
            className='btn btn-light m-0'
            disabled={busy}
            onClick={() => setShow(false)}
          >
            Zatvori
          </button>
          {nextId !== null && (
            <button
              type='button'
              className='btn btn-primary m-0'
              disabled={busy}
              onClick={() => void runBatch(nextId)}
            >
              {busy ? 'Osvježavanje…' : error ? 'Pokušaj ponovo' : 'Osvježi dalje'}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}
